/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { FiChevronDown } from 'react-icons/fi'

interface OptionFilterProps<T extends string> {
  label: string
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}

/** A one-of-N toolbar dropdown, styled like the asset map's Gateway filter. */
export const OptionFilter = <T extends string>({ label, options, value, onChange }: OptionFilterProps<T>) => {
  const current = options.find((o) => o.value === value)?.label ?? options[0]?.label

  const select = (next: T) => {
    onChange(next)
    // daisyUI's dropdown stays open while it has focus.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
  }

  return (
    <div className="dropdown" data-filter-dropdown>
      <div
        tabIndex={0}
        role="button"
        aria-label={`${label} filter`}
        className="btn btn-sm btn-outline min-w-[135px] flex items-center justify-between font-normal"
      >
        <span>
          {label}: {current}
        </span>
        <FiChevronDown className="w-4 h-4 text-base-content/60" />
      </div>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-base-100 rounded-lg shadow-lg border border-base-300 z-20 w-full p-1 mt-1"
      >
        {options.map((opt) => (
          <li key={opt.value}>
            <button onClick={() => select(opt.value)} className={`text-xs ${value === opt.value ? 'active' : ''}`}>
              {label}: {opt.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
